import { useEffect, useRef, useState } from 'react'
import { SignatureCanvas } from '@/components/SignatureCanvas'
import type { CreatePersonalRapportRequest, RapportSignatur } from '@/lib/api'
import { rapportFehlerText, type RapportAuftrag } from '@/lib/rapport'

type Schritt = 'auftragnehmer' | 'auftraggeber' | 'senden'

interface Props {
  auftrag: RapportAuftrag
  mitarbeiterName: string
  /** Erzeugt den Rapport; wirft bei Fehlern (Text kommt aus rapportFehlerText). */
  onErstellen: (signaturen: CreatePersonalRapportRequest['signaturen']) => Promise<void>
  onSchliessen: () => void
}

/** Unterschrift vor Ort, beide Schritte ueberspringbar (E-520). Escape schliesst, solange kein
 * Aufruf laeuft; Fokus wandert in den Dialog und beim Schliessen zurueck; Tab bleibt im Dialog. */
export function RapportDialog({ auftrag, mitarbeiterName, onErstellen, onSchliessen }: Props) {
  const [schritt, setSchritt] = useState<Schritt>('auftragnehmer')
  const [nameAn, setNameAn] = useState(mitarbeiterName)
  const [nameAg, setNameAg] = useState(auftrag.auftraggeberName)
  const [sigAn, setSigAn] = useState<RapportSignatur | undefined>()
  const [sigAg, setSigAg] = useState<RapportSignatur | undefined>()
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const laeuft = useRef(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const zuvorFokus = useRef<Element | null>(document.activeElement)
  const busyRef = useRef(false)
  busyRef.current = busy

  useEffect(() => {
    const vorher = zuvorFokus.current
    boxRef.current?.focus()
    return () => {
      if (vorher instanceof HTMLElement) vorher.focus()
    }
  }, [])

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      if (!busyRef.current) onSchliessen()
      return
    }
    if (e.key !== 'Tab' || !boxRef.current) return
    const el = Array.from(
      boxRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input, canvas, [href]'),
    )
    if (el.length === 0) return
    const erstes = el[0]
    const letztes = el[el.length - 1]
    const aktiv = document.activeElement
    if (e.shiftKey && (aktiv === erstes || aktiv === boxRef.current)) {
      e.preventDefault()
      letztes.focus()
    } else if (!e.shiftKey && aktiv === letztes) {
      e.preventDefault()
      erstes.focus()
    }
  }

  async function senden(an?: RapportSignatur, ag?: RapportSignatur) {
    if (laeuft.current) return
    laeuft.current = true
    setBusy(true)
    setFehler(null)
    setSchritt('senden')
    try {
      await onErstellen(an || ag ? { auftragnehmer: an, auftraggeber: ag } : undefined)
    } catch (e) {
      setFehler(rapportFehlerText(e))
    } finally {
      laeuft.current = false
      setBusy(false)
    }
  }

  function signiert(wer: 'auftragnehmer' | 'auftraggeber', png: string) {
    const name = (wer === 'auftragnehmer' ? nameAn : nameAg).trim()
    if (!name) {
      setHinweis('Bitte zuerst den Namen eintragen.')
      return
    }
    setHinweis(null)
    const sig = { unterzeichnerName: name, signaturPngBase64: png }
    if (wer === 'auftragnehmer') {
      setSigAn(sig)
      setSchritt('auftraggeber')
    } else {
      setSigAg(sig)
      void senden(sigAn, sig)
    }
  }

  function ueberspringen(wer: 'auftragnehmer' | 'auftraggeber') {
    setHinweis(null)
    if (wer === 'auftragnehmer') {
      setSigAn(undefined)
      setSchritt('auftraggeber')
    } else {
      setSigAg(undefined)
      void senden(sigAn, undefined)
    }
  }

  const titelId = 'rapport-dialog-titel'
  return (
    <div className="rapport-overlay">
      <div
        ref={boxRef}
        className="card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{ maxHeight: '100%', overflow: 'auto' }}
      >
        <h2 id={titelId} style={{ marginTop: 0 }}>Rapport generieren</h2>
        <p className="hint">
          {auftrag.auftragsnummer} — {auftrag.auftraggeberName}. Die Unterschrift ist freiwillig.
        </p>
        {hinweis && <p className="error-text" role="alert">{hinweis}</p>}
        {fehler && <p className="error-text" role="alert">{fehler}</p>}

        {schritt === 'auftragnehmer' && (
          <>
            <input
              className="input"
              aria-label="Name Mitarbeiter"
              placeholder="Name Mitarbeiter"
              value={nameAn}
              onChange={(e) => setNameAn(e.target.value)}
            />
            <SignatureCanvas
              label="Unterschrift Mitarbeiter"
              onDone={(png) => signiert('auftragnehmer', png)}
            />
            <button className="button button-secondary" onClick={() => ueberspringen('auftragnehmer')}>
              Ohne Unterschrift weiter
            </button>
          </>
        )}

        {schritt === 'auftraggeber' && (
          <>
            <p>Gerät bitte jetzt dem Kunden übergeben.</p>
            <input
              className="input"
              aria-label="Name Auftraggeber"
              placeholder="Name Auftraggeber"
              value={nameAg}
              onChange={(e) => setNameAg(e.target.value)}
            />
            <SignatureCanvas
              label="Unterschrift Auftraggeber (Kunde)"
              onDone={(png) => signiert('auftraggeber', png)}
            />
            <button className="button button-secondary" onClick={() => ueberspringen('auftraggeber')}>
              Ohne Unterschrift weiter
            </button>
          </>
        )}

        {schritt === 'senden' && (
          <>
            {busy && <p className="hint" role="status">Rapport wird erstellt …</p>}
            {!busy && fehler && (
              <button className="button button-in" onClick={() => void senden(sigAn, sigAg)}>
                Erneut versuchen
              </button>
            )}
          </>
        )}

        <button className="button button-secondary" onClick={onSchliessen} disabled={busy}>
          Abbrechen
        </button>
      </div>
    </div>
  )
}
